import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartEvent,
  ActiveElement,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { useBuildingBacklogEvolution } from "../hooks/useStatisticsData";
import { PeriodTicketMovement } from "../types/statistics.types";
import { PeriodMovementsModal } from "./PeriodMovementsModal";
import {
  TrendUp,
  TrendDown,
  Building,
  ArrowUp,
  ArrowDown,
  ChartLine,
  Trophy,
  Warning,
  WarningCircleIcon,
  MagnifyingGlass,
  CaretDown,
  Check,
  X,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Distinct colors for building lines
const BUILDING_COLORS = [
  "#EA8162", // coral
  "#5B7304", // moss
  "#3B82F6", // blue
  "#8B5CF6", // purple
  "#F59E0B", // amber
  "#10B981", // emerald
  "#EC4899", // pink
  "#6366F1", // indigo
  "#14B8A6", // teal
  "#F97316", // orange
  "#22C55E", // green
  "#EF4444", // red
  "#06B6D4", // cyan
  "#A855F7", // violet
  "#84CC16", // lime
];

// Ticket source options - values must match database enum (TicketSource)
const SOURCE_OPTIONS = [
  { value: "Huurder", label: "Huurder" },
  { value: "Intern", label: "Intern" },
  { value: "Buur", label: "Buur" },
  { value: "Onbekend", label: "Onbekend" },
  { value: "all", label: "Alle bronnen" },
];

export function BuildingVolumeTrendsCharts() {
  const [granularity, setGranularity] = useState<"week" | "month">("week");
  const [source, setSource] = useState<string>("Huurder");
  const [selectedBuildings, setSelectedBuildings] = useState<Set<string>>(
    new Set(),
  );
  const [buildingSearch, setBuildingSearch] = useState("");
  const [buildingPickerOpen, setBuildingPickerOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] =
    useState<PeriodTicketMovement | null>(null);
  const [periodModalOpen, setPeriodModalOpen] = useState(false);
  const chartRef = useRef<ChartJS<"line">>(null);

  const { data, loading } = useBuildingBacklogEvolution(
    granularity,
    source === "all" ? undefined : source,
  );
  const navigate = useNavigate();

  // Handle chart click to show period details
  const handleChartClick = useCallback(
    (event: ChartEvent, elements: ActiveElement[]) => {
      if (!data?.periodMovements || !chartRef.current) return;

      // Get the index from the first element if clicked on a point
      // Otherwise, try to get the index from the x-axis click
      let periodIndex: number | null = null;

      if (elements.length > 0) {
        periodIndex = elements[0].index;
      } else {
        // Get x position and convert to data index
        const chart = chartRef.current;
        const x = event.x;
        if (x !== null && x !== undefined) {
          const xAxis = chart.scales.x;
          const index = Math.round(
            (x - xAxis.left) / (xAxis.width / (data.periods.length - 1)),
          );
          if (index >= 0 && index < data.periods.length) {
            periodIndex = index;
          }
        }
      }

      if (periodIndex !== null && periodIndex >= 0) {
        const periodKey = data.periodKeys[periodIndex];
        const periodData = data.periodMovements.find(
          (pm) => pm.periodKey === periodKey,
        );

        if (periodData) {
          // Filter movements by selected buildings
          const filteredPeriodData: PeriodTicketMovement = {
            ...periodData,
            created:
              selectedBuildings.size > 0
                ? periodData.created.filter((ticket) =>
                    selectedBuildings.has(ticket.buildingId),
                  )
                : periodData.created,
            completed:
              selectedBuildings.size > 0
                ? periodData.completed.filter((ticket) =>
                    selectedBuildings.has(ticket.buildingId),
                  )
                : periodData.completed,
          };

          setSelectedPeriod(filteredPeriodData);
          setPeriodModalOpen(true);
        }
      }
    },
    [data, selectedBuildings],
  );

  // Initialize selected buildings when data loads
  useEffect(() => {
    if (data?.buildings && selectedBuildings.size === 0) {
      // Default: select top 10 buildings by current open count
      const top10 = [...data.buildings]
        .sort((a, b) => b.currentOpenCount - a.currentOpenCount)
        .slice(0, 10)
        .map((b) => b.buildingId);
      setSelectedBuildings(new Set(top10));
    }
  }, [data?.buildings]);

  // Filter buildings by search
  const filteredBuildings = useMemo(() => {
    if (!data?.buildings) return [];
    const searchLower = buildingSearch.toLowerCase();
    return data.buildings
      .filter(
        (b) =>
          b.buildingId.toLowerCase().includes(searchLower) ||
          b.buildingName.toLowerCase().includes(searchLower),
      )
      .sort((a, b) => b.currentOpenCount - a.currentOpenCount);
  }, [data?.buildings, buildingSearch]);

  // Toggle building selection
  const toggleBuilding = (buildingId: string) => {
    setSelectedBuildings((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(buildingId)) {
        newSet.delete(buildingId);
      } else {
        newSet.add(buildingId);
      }
      return newSet;
    });
  };

  // Select all visible buildings
  const selectAllBuildings = () => {
    const allIds = filteredBuildings.map((b) => b.buildingId);
    setSelectedBuildings(new Set(allIds));
  };

  // Deselect all buildings
  const deselectAllBuildings = () => {
    setSelectedBuildings(new Set());
  };

  // Line chart data - evolution of open tickets per building over time
  const evolutionChartData = useMemo(() => {
    if (!data?.buildings || data.buildings.length === 0) return null;

    // Filter to selected buildings only
    const selectedBuildingsData = data.buildings.filter((b) =>
      selectedBuildings.has(b.buildingId),
    );

    if (selectedBuildingsData.length === 0) return null;

    return {
      labels: data.periods,
      datasets: selectedBuildingsData.map((building, index) => ({
        label:
          building.buildingName.length > 25
            ? building.buildingName.substring(0, 25) + "..."
            : building.buildingName,
        data: building.openCounts,
        borderColor: BUILDING_COLORS[index % BUILDING_COLORS.length],
        backgroundColor: `${BUILDING_COLORS[index % BUILDING_COLORS.length]}20`,
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 6,
        borderWidth: 2,
      })),
    };
  }, [data, selectedBuildings]);

  // Bar chart data - buildings with most accumulated tickets
  const topBuildingsChartData = useMemo(() => {
    if (!data?.topBuildingsByVolume || data.topBuildingsByVolume.length === 0)
      return null;

    return {
      labels: data.topBuildingsByVolume.map((b) =>
        b.buildingName.length > 20
          ? b.buildingName.substring(0, 20) + "..."
          : b.buildingName,
      ),
      datasets: [
        {
          label: "Open tickets",
          data: data.topBuildingsByVolume.map((b) => b.currentOpenCount),
          backgroundColor: data.topBuildingsByVolume.map(
            (_, i) => BUILDING_COLORS[i % BUILDING_COLORS.length],
          ),
          borderColor: data.topBuildingsByVolume.map(
            (_, i) => BUILDING_COLORS[i % BUILDING_COLORS.length],
          ),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [data]);

  const evolutionChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      onClick: handleChartClick,
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            usePointStyle: true,
            padding: 15,
            boxWidth: 8,
          },
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#EA8162",
          borderWidth: 1,
          padding: 12,
          callbacks: {
            afterTitle: () => "Klik om details te bekijken",
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(49, 64, 59, 0.1)",
          },
          title: {
            display: true,
            text: "Open tickets",
            font: {
              size: 11,
            },
          },
        },
      },
    }),
    [handleChartClick],
  );

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#31403B",
        titleColor: "#F9F0EE",
        bodyColor: "#F9F0EE",
        borderColor: "#EA8162",
        borderWidth: 1,
        padding: 12,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: "rgba(49, 64, 59, 0.1)",
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  const navigateToBuilding = (buildingId: string) => {
    navigate(`/helpdesk?buildingId=${buildingId}`);
  };

  // Get label for current source
  const sourceLabel =
    SOURCE_OPTIONS.find((s) => s.value === source)?.label || "Alle bronnen";

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Evolution Chart Skeleton */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ChartLine className="h-5 w-5" />
                <Skeleton className="h-5 w-64" />
              </div>
              <Skeleton className="h-9 w-32" />
            </div>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[400px] w-full" />
          </CardContent>
        </Card>

        {/* Other sections skeleton */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[250px] w-full" />
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[250px] w-full" />
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[250px] w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Evolution Line Chart - Full width */}
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <ChartLine className="text-primary h-5 w-5" weight="duotone" />
              <CardTitle className="text-lg">
                Evolutie open tickets per gebouw
              </CardTitle>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Source Filter */}
              <Select
                value={source}
                onValueChange={(value) => setSource(value)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Bron" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Granularity Filter */}
              <Select
                value={granularity}
                onValueChange={(value: "week" | "month") =>
                  setGranularity(value)
                }
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Periode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Per week</SelectItem>
                  <SelectItem value="month">Per maand</SelectItem>
                </SelectContent>
              </Select>

              {/* Building Multi-Select */}
              <Popover
                open={buildingPickerOpen}
                onOpenChange={setBuildingPickerOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="min-w-[180px] justify-between"
                  >
                    <span className="truncate">
                      {selectedBuildings.size === 0
                        ? "Selecteer gebouwen"
                        : `${selectedBuildings.size} gebouw${selectedBuildings.size === 1 ? "" : "en"}`}
                    </span>
                    <CaretDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="end">
                  <div className="p-3 border-b">
                    <div className="relative">
                      <MagnifyingGlass className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                      <Input
                        placeholder="Zoek gebouw..."
                        value={buildingSearch}
                        onChange={(e) => setBuildingSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-muted-foreground text-xs">
                        {filteredBuildings.length} gebouwen
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={selectAllBuildings}
                        >
                          <Check className="mr-1 h-3 w-3" />
                          Alles
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={deselectAllBuildings}
                        >
                          <X className="mr-1 h-3 w-3" />
                          Geen
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto p-2">
                    {filteredBuildings.length === 0 ? (
                      <div className="text-muted-foreground py-4 text-center text-sm">
                        Geen gebouwen gevonden
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredBuildings.map((building) => (
                          <label
                            key={building.buildingId}
                            className="hover:bg-muted/50 flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 transition-colors"
                          >
                            <Checkbox
                              checked={selectedBuildings.has(
                                building.buildingId,
                              )}
                              onCheckedChange={() =>
                                toggleBuilding(building.buildingId)
                              }
                            />
                            <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                              <span className="truncate text-sm">
                                {building.buildingName}
                              </span>
                              <Badge variant="outline" className="shrink-0">
                                {building.currentOpenCount}
                              </Badge>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <p className="text-muted-foreground text-xs">
            Bron: {sourceLabel} • {selectedBuildings.size} gebouw
            {selectedBuildings.size === 1 ? "" : "en"} geselecteerd •{" "}
            <span className="text-primary/70">
              Klik op een periode voor ticket details
            </span>
          </p>
        </CardHeader>
        <CardContent>
          {evolutionChartData ? (
            <div className="h-[400px] cursor-pointer">
              <Line
                ref={chartRef}
                data={evolutionChartData}
                options={evolutionChartOptions}
              />
            </div>
          ) : (
            <div className="text-muted-foreground flex h-[400px] flex-col items-center justify-center">
              <ChartLine className="mb-2 h-12 w-12 opacity-50" />
              <p>
                {selectedBuildings.size === 0
                  ? "Selecteer gebouwen om de grafiek te tonen"
                  : "Geen data beschikbaar"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Three column layout for rankings and trends */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Top Buildings by Volume */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <WarningCircleIcon
                className="h-5 w-5 text-amber-500"
                weight="duotone"
              />
              <CardTitle className="text-lg">Meeste open tickets</CardTitle>
            </div>
            <p className="text-muted-foreground text-xs">
              Gebouwen met de meeste openstaande tickets ({sourceLabel})
            </p>
          </CardHeader>
          <CardContent>
            {topBuildingsChartData ? (
              <>
                <div className="h-[200px]">
                  <Bar data={topBuildingsChartData} options={barChartOptions} />
                </div>
                <div className="mt-4 max-h-[150px] space-y-1 overflow-y-auto">
                  {data?.topBuildingsByVolume.map((building) => (
                    <button
                      key={building.buildingId}
                      onClick={() => navigateToBuilding(building.buildingId)}
                      className="hover:bg-muted/50 flex w-full items-center justify-between rounded-lg p-2 text-left transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground w-5 text-xs font-bold">
                          #{building.rank}
                        </span>
                        <Building className="text-muted-foreground h-4 w-4" />
                        <span className="max-w-[140px] truncate text-sm">
                          {building.buildingName}
                        </span>
                      </div>
                      <Badge variant="outline">
                        {building.currentOpenCount}
                      </Badge>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-muted-foreground flex h-[300px] flex-col items-center justify-center">
                <Trophy className="mb-2 h-12 w-12 opacity-50" />
                <p>Geen data beschikbaar</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rising Buildings */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Warning className="h-5 w-5 text-red-500" weight="duotone" />
              <CardTitle className="text-lg">
                Gebouwen met stijgende tickets
              </CardTitle>
            </div>
            <p className="text-muted-foreground text-xs">
              Stijging t.o.v. {data?.comparisonLabel || "vorige periode"}
            </p>
          </CardHeader>
          <CardContent>
            {data?.risingBuildings && data.risingBuildings.length > 0 ? (
              <div className="max-h-[350px] space-y-2 overflow-y-auto">
                {data.risingBuildings.map((building) => (
                  <button
                    key={building.buildingId}
                    onClick={() => navigateToBuilding(building.buildingId)}
                    className="hover:bg-muted/50 flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors"
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <Building className="text-muted-foreground h-4 w-4" />
                        <span className="max-w-[150px] truncate text-sm font-medium">
                          {building.buildingName}
                        </span>
                      </div>
                      <span className="text-muted-foreground mt-1 text-xs">
                        {building.previousOpenCount} &rarr;{" "}
                        {building.currentOpenCount} open
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge
                        variant="destructive"
                        className="flex items-center gap-1"
                      >
                        <ArrowUp className="h-3 w-3" />+
                        {building.absoluteChange}
                      </Badge>
                      {building.percentageChange > 0 && (
                        <span className="text-xs text-red-500">
                          +{building.percentageChange}%
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-muted-foreground flex h-[300px] flex-col items-center justify-center">
                <TrendUp className="mb-2 h-12 w-12 opacity-50" />
                <p>Geen gebouwen met stijgende trend</p>
                <p className="mt-1 text-xs">Goed bezig!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Declining Buildings */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <TrendDown className="h-5 w-5 text-green-500" weight="duotone" />
              <CardTitle className="text-lg">
                Gebouwen met dalend aantal tickets
              </CardTitle>
            </div>
            <p className="text-muted-foreground text-xs">
              Daling t.o.v. {data?.comparisonLabel || "vorige periode"}
            </p>
          </CardHeader>
          <CardContent>
            {data?.decliningBuildings && data.decliningBuildings.length > 0 ? (
              <div className="max-h-[350px] space-y-2 overflow-y-auto">
                {data.decliningBuildings.map((building) => (
                  <button
                    key={building.buildingId}
                    onClick={() => navigateToBuilding(building.buildingId)}
                    className="hover:bg-muted/50 flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors"
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <Building className="text-muted-foreground h-4 w-4" />
                        <span className="max-w-[150px] truncate text-sm font-medium">
                          {building.buildingName}
                        </span>
                      </div>
                      <span className="text-muted-foreground mt-1 text-xs">
                        {building.previousOpenCount} &rarr;{" "}
                        {building.currentOpenCount} open
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className="flex items-center gap-1 bg-green-500">
                        <ArrowDown className="h-3 w-3" />
                        {building.absoluteChange}
                      </Badge>
                      {building.percentageChange < 0 && (
                        <span className="text-xs text-green-600">
                          {building.percentageChange}%
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-muted-foreground flex h-[300px] flex-col items-center justify-center">
                <TrendDown className="mb-2 h-12 w-12 opacity-50" />
                <p>Geen gebouwen met dalende trend</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Period Movements Modal */}
      <PeriodMovementsModal
        isOpen={periodModalOpen}
        onClose={() => setPeriodModalOpen(false)}
        periodData={selectedPeriod}
      />
    </div>
  );
}
